/* eslint-disable */

import axios from 'axios';
import axiosConfig from '../axiosConfig';
import {
	ChatRequest,
	ChatResponse,
	ChatSession,
	ChatSessionHistory,
	ChatSessionsResponse,
	GenericResponse,
	InovationChatRequest,
	InovationChatResponse,
} from '../types';

const API_KEY =
	process.env.REACT_APP_INNOVATION_AI_API_KEY ??
	'c2051712053c930e9345e66e24a58de140f33e8be8ba6c01b242358774ef4f81';

export class ChatActions {
	public static SendMessage(request: ChatRequest): Promise<GenericResponse<ChatResponse>> {
		const url = 'app/ai/chat';

		return axiosConfig.instance.post(url, request).then((res) => {
			return res.data as GenericResponse<ChatResponse>;
		});
	}

	/**
	 * Sends a message to the Innovation AI endpoint (used by Analyst AI page).
	 * Base URL is constructed from env vars:
	 *  - REACT_APP_INNOVATION_AI_API_ID
	 *  - REACT_APP_INNOVATION_AI_ENVIRONMENT
	 * Falls back to the hardcoded URL if env vars are missing.
	 */
	public static sendInovationAIMessage(
		request: InovationChatRequest
	): Promise<InovationChatResponse> {
		const cfUrl =
			process.env.REACT_APP_INNOVATION_AI_API_URL ??
			'https://ai-analyst.zeroedin-dev.com/chat';

		// Minimal axios to avoid XSRF header and cookies
		const directAxios = axios.create({
			withCredentials: false,
			timeout: 100000,
		});

		return directAxios
			.post(cfUrl, request, {
				headers: {
					'Content-Type': 'application/json',
					'x-api-key': API_KEY,
					'x-actor-id': request.userId,
				},
			})
			.then((res) => {
				const data = res.data;
				if (res.status === 202) {
					const jobId = data?.job_id ?? data?.jobId ?? data?.job ?? '';
					return {
						session_id: data?.session_id ?? '',
						job_id: jobId,
						response: 'the response is taking longer than expected to complete',
						status: 'pending',
						trace: data?.trace ?? [],
					} as InovationChatResponse;
				}

				if (typeof data === 'string') {
					return {
						session_id: '',
						response: data,
						status: 'complete',
						trace: [],
					} as InovationChatResponse;
				}

				return data as InovationChatResponse;
			})
			.catch((err) => {
				console.error('Direct CloudFront call failed:', err?.message ?? err);
				return {
					session_id: '',
					response: '',
					status: 'error',
					trace: [],
				} as InovationChatResponse;
			});
	}

	/**
	 * Polls the Innovation AI status endpoint for a long-running job_id until completion.
	 */
	public static getInovationAIStatus(jobId: string): Promise<InovationChatResponse> {
		const cfBase =
			process.env.REACT_APP_INNOVATION_AI_API_URL ??
			'https://ai-analyst.zeroedin-dev.com/chat';
		const statusUrl = cfBase.endsWith('/chat') ? `${cfBase}/status` : `${cfBase}/chat/status`;

		const directAxios = axios.create({
			withCredentials: false,
			timeout: 100000,
			xsrfCookieName: '',
			xsrfHeaderName: '',
		});

		return directAxios
			.get(statusUrl, {
				params: { job_id: jobId },
				headers: { 'x-api-key': API_KEY },
			})
			.then((res) => {
				const data = res.data;
				if (res.status === 200) {
					if (typeof data === 'string') {
						return {
							session_id: '',
							response: data,
							status: 'complete',
							trace: [],
						} as InovationChatResponse;
					}

					return {
						session_id: data?.session_id ?? '',
						job_id: data?.job_id ?? jobId,
						response: data?.response ?? (typeof data === 'string' ? data : ''),
						status:
							data?.status === 'complete' ? 'complete' : data?.status ?? 'pending',
						trace: data?.trace ?? [],
					} as InovationChatResponse;
				}

				return {
					session_id: '',
					response: '',
					status: 'error',
					trace: [],
				} as InovationChatResponse;
			})
			.catch((err) => {
				console.error('Direct status check failed:', err?.message ?? err);
				return {
					session_id: '',
					response: '',
					status: 'error',
					trace: [],
				} as InovationChatResponse;
			});
	}

	public static getInovationAIChatHistory(
		jobId: string,
		sessionId: string
	): Promise<ChatSession> {
		const cfBase =
			process.env.REACT_APP_INNOVATION_AI_API_URL ??
			'https://ai-analyst.zeroedin-dev.com/chat';
		let statusUrl = cfBase.endsWith('/chat') ? `${cfBase}/history` : `${cfBase}/chat/history`;

		const directAxios = axios.create({
			withCredentials: false,
			timeout: 100000,
			xsrfCookieName: '',
			xsrfHeaderName: '',
		});

		return directAxios
			.get(statusUrl, {
				params: {
					job_id: jobId,
					session_id: sessionId,
				},
				headers: { 'x-api-key': API_KEY },
			})
			.then((res) => {
				const data = res.data;

				if (res.status === 200) {
					if (typeof data === 'string') {
						return {
							session_id: '',
							messages: [
								{
									role: 'assistant',
									content: data,
									timestamp: Date.now(),
								},
							],
						} as ChatSession;
					}

					return {
						session_id: data?.session_id ?? '',
						messages: data?.messages ?? [],
					} as ChatSession;
				}

				return {
					session_id: '',
					messages: [],
				} as ChatSession;
			})
			.catch((err) => {
				console.error('Direct status check failed:', err?.message ?? err);

				return {
					session_id: '',
					messages: [],
				} as ChatSession;
			});
	}

	/**
	 * Get all chat sessions for the current user
	 * Currently mocked - will switch to real endpoint when available
	 * Real endpoint: GET /chat/sessions
	 */
	public static getInovationAIChatSessions(
		limit = 25,
		userId = ''
	): Promise<ChatSessionsResponse> {
		const cfBase =
			process.env.REACT_APP_INNOVATION_AI_API_URL ??
			'https://ai-analyst.zeroedin-dev.com/chat';

		const sessionsUrl = cfBase.endsWith('/chat')
			? `${cfBase}/sessions`
			: `${cfBase}/chat/sessions`;

		const directAxios = axios.create({
			withCredentials: false,
			timeout: 100000,
			xsrfCookieName: '',
			xsrfHeaderName: '',
		});

		return directAxios
			.get(sessionsUrl, {
				params: {
					limit,
				},
				headers: {
					'x-actor-id': userId,
					'x-api-key': API_KEY,
				},
			})
			.then((res) => {
				const data = res.data;

				if (res.status === 200) {
					return {
						sessions: data?.sessions ?? [],
					} as ChatSessionsResponse;
				}

				return {
					sessions: [],
				} as ChatSessionsResponse;
			})
			.catch((err) => {
				console.error('Failed to fetch chat sessions:', err?.message ?? err);

				return {
					sessions: [],
				} as ChatSessionsResponse;
			});
	}
}
